import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reminders`);
      const data = await response.json();
      setReminders(data.reminders || []);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high':
        return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
      case 'medium':
        return { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1f4e]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Follow-up Reminders</h1>
          <p className="text-gray-500 text-sm mt-1">Quotes that may need attention</p>
        </div>
        <Button
          onClick={fetchReminders}
          variant="outline"
          className="h-10 px-4 rounded-lg"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-8">
        <p className="text-sm text-blue-800">
          <strong>Internal reminders only</strong> — These are not visible to clients. Follow up via phone or email as needed.
        </p>
      </div>

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
          <p className="text-gray-500">
            No quotes require follow-up at this time.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reminders.map((reminder, index) => {
            const priorityConfig = getPriorityConfig(reminder.priority);
            
            return (
              <div
                key={reminder.quote_id + index}
                onClick={() => navigate(`/admin/quotes/${reminder.quote_id}`)}
                className={`bg-white rounded-xl border ${priorityConfig.border} p-5 cursor-pointer hover:shadow-md transition-shadow`}
                data-testid={`reminder-${reminder.quote_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${priorityConfig.bg}`}>
                      <AlertTriangle className={`w-5 h-5 ${priorityConfig.color}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{reminder.company_name}</h3>
                      <p className="text-sm text-gray-500">{reminder.site_location}</p>
                      <p className="text-sm text-gray-600 mt-2">{reminder.reason}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>Quote ID: {reminder.quote_id}</span>
                        {reminder.created_at && (
                          <span>Created: {formatDate(reminder.created_at)}</span>
                        )}
                        {reminder.first_viewed_at && (
                          <span>Viewed: {formatDate(reminder.first_viewed_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${priorityConfig.bg} ${priorityConfig.color} capitalize`}>
                      {reminder.priority}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Reminders;
